import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface ParticleFieldProps {
  count?: number;
  radius?: number;
  colors?: [string, string];
  mousePosition: { x: number; y: number };
}

const ParticleField = ({
  count = 2000,
  radius = 10,
  colors = ['#6C5CE7', '#1F51FF'],
  mousePosition,
}: ParticleFieldProps) => {
  const meshRef = useRef<THREE.Points>(null);
  const timeRef = useRef(0);

  // Create particle geometry and attributes
  const { positions, randoms, sizes } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const randoms = new Float32Array(count);
    const sizes = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      // Distribute particles in a sphere
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = radius * Math.cbrt(Math.random());

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      randoms[i] = Math.random();
      sizes[i] = Math.random() * 2 + 1;
    }

    return { positions, randoms, sizes };
  }, [count, radius]);

  // Create shader material
  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uMouse: { value: new THREE.Vector2(0, 0) },
        uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        uColorA: { value: new THREE.Color(colors[0]) },
        uColorB: { value: new THREE.Color(colors[1]) },
      },
      vertexShader: `
        attribute float aRandom;
        attribute float aSize;
        
        uniform float uTime;
        uniform vec2 uMouse;
        uniform vec2 uResolution;
        
        varying vec3 vColor;
        varying float vAlpha;
        
        void main() {
          vec3 pos = position;
          
          float wave = sin(pos.x * 2.0 + uTime * 0.5) * cos(pos.y * 2.0 + uTime * 0.3) * 0.3;
          pos.z += wave * aRandom;
          
          vec2 mouseInfluence = (uMouse / uResolution) * 2.0 - 1.0;
          float dist = distance(pos.xy, mouseInfluence * 10.0);
          float force = 1.0 / (dist * 0.5 + 1.0);
          pos.xy += normalize(pos.xy - mouseInfluence * 10.0) * force * 0.5;
          
          float depth = (pos.z + 5.0) / 10.0;
          
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = aSize * (300.0 / -mvPosition.z) * depth;
          
          vColor = mix(vec3(0.424, 0.361, 0.906), vec3(0.122, 0.318, 1.0), aRandom);
          vAlpha = depth * (0.6 + aRandom * 0.4);
        }
      `,
      fragmentShader: `
        uniform vec3 uColorA;
        uniform vec3 uColorB;
        uniform float uTime;
        
        varying vec3 vColor;
        varying float vAlpha;
        
        void main() {
          vec2 xy = gl_PointCoord.xy - vec2(0.5);
          float dist = length(xy);
          
          if (dist > 0.5) {
            discard;
          }
          
          float alpha = smoothstep(0.5, 0.0, dist);
          float pulse = sin(uTime * 2.0) * 0.2 + 0.8;
          vec3 color = mix(uColorA, uColorB, vColor.r);
          
          gl_FragColor = vec4(color, alpha * vAlpha * pulse);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
  }, [colors]);

  // Animation loop
  useFrame(() => {
    if (!meshRef.current) return;

    timeRef.current += 0.01;
    const material = meshRef.current.material as THREE.ShaderMaterial;
    material.uniforms.uTime.value = timeRef.current;
    material.uniforms.uMouse.value.set(mousePosition.x, mousePosition.y);

    // Gentle rotation
    meshRef.current.rotation.y += 0.0005;
    meshRef.current.rotation.x = Math.sin(timeRef.current * 0.3) * 0.1;
  });

  return (
    <points ref={meshRef} material={material}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
          args={[positions, 3]}
        />
        <bufferAttribute
          attach="attributes-aRandom"
          count={randoms.length}
          array={randoms}
          itemSize={1}
          args={[randoms, 1]}
        />
        <bufferAttribute
          attach="attributes-aSize"
          count={sizes.length}
          array={sizes}
          itemSize={1}
          args={[sizes, 1]}
        />
      </bufferGeometry>
    </points>
  );
};

export default ParticleField;
